import React from 'react';

import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';

import styles from './Tooltip.module.scss';

export interface TooltipProps extends Omit<MuiTooltipProps, 'title'> {
    /**
     * The content to display in the tooltip
     */
    content: React.ReactNode;
    /**
     * Optional title for the tooltip (creates a "With Title" variant)
     */
    title?: string;
    /**
     * The element that triggers the tooltip
     */
    children: React.ReactElement;
}

/**
 * Custom Tooltip component based on MUI Tooltip
 * Implements design system styling from Figma atoms Tooltip component
 * Supports light and dark themes via CSS variables
 * Two variants: Simple (content only) and With Title (title + divider + content)
 */
export const Tooltip: React.FC<TooltipProps> = ({ content, title, children, ...props }) => {
    // Create custom tooltip content
    const tooltipContent = title ? (
        <div className={styles.tooltipWithTitle}>
            <div className={styles.tooltipTitle}>{title}</div>
            <div className={styles.tooltipDivider} />
            <div className={styles.tooltipText}>{content}</div>
        </div>
    ) : (
        <div className={styles.tooltipSimple}>{content}</div>
    );

    return (
        <MuiTooltip
            title={tooltipContent}
            classes={{
                tooltip: styles.tooltip,
                arrow: styles.arrow,
            }}
            {...props}
        >
            {children}
        </MuiTooltip>
    );
};

export default Tooltip;
